import axiosInstance from '../axiosConfig';

export interface Department {
    id: number;
    name: string;
    theme_color: string;
    theme_classes: {
        bgColor: string;
        borderColor: string;
        textColor: string;
        linkBg: string;
        hoverBg: string;
        ring: string;
    };
    url: string;
    hashtags: string | null;
    title: string | null;
    description: string;
    order: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface DepartmentsResponse {
    success: boolean;
    data: Department[];
    message: string;
    count: number;
    meta?: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
    };
}

export interface DepartmentResponse {
    success: boolean;
    data: Department;
    message: string;
}

export const themeColors = [
    { value: 'blue', label: 'Blue', bgClass: 'bg-blue-100', textClass: 'text-blue-700', borderClass: 'border-blue-500', linkClass: 'bg-blue-500' },
    { value: 'green', label: 'Green', bgClass: 'bg-green-100', textClass: 'text-green-700', borderClass: 'border-green-500', linkClass: 'bg-green-500' },
    { value: 'red', label: 'Red', bgClass: 'bg-red-100', textClass: 'text-red-700', borderClass: 'border-red-500', linkClass: 'bg-red-500' },
    { value: 'yellow', label: 'Yellow', bgClass: 'bg-yellow-100', textClass: 'text-yellow-700', borderClass: 'border-yellow-500', linkClass: 'bg-yellow-500' },
    { value: 'purple', label: 'Purple', bgClass: 'bg-purple-100', textClass: 'text-purple-700', borderClass: 'border-purple-500', linkClass: 'bg-purple-500' },
    { value: 'pink', label: 'Pink', bgClass: 'bg-pink-100', textClass: 'text-pink-700', borderClass: 'border-pink-500', linkClass: 'bg-pink-500' },
    { value: 'indigo', label: 'Indigo', bgClass: 'bg-indigo-100', textClass: 'text-indigo-700', borderClass: 'border-indigo-500', linkClass: 'bg-indigo-500' },
    { value: 'orange', label: 'Orange', bgClass: 'bg-orange-100', textClass: 'text-orange-700', borderClass: 'border-orange-500', linkClass: 'bg-orange-500' },
];

export const departmentService = {
    // Initialize CSRF token
    async initializeCsrf() {
        try {
            await axiosInstance.get('/sanctum/csrf-cookie');
            console.log('CSRF token initialized');
        } catch (error) {
            console.error('Failed to initialize CSRF token:', error);
        }
    },

    // Get all departments with pagination and search
    async getAll(params?: { page?: number; per_page?: number; search?: string }): Promise<DepartmentsResponse> {
        try {
            const response = await axiosInstance.get('/api/admin/departments', { params });
            return response.data;
        } catch (error) {
            console.error('Error in getAll:', error);
            throw error;
        }
    },

    // Get single department by ID
    async getById(id: number): Promise<DepartmentResponse> {
        try {
            const response = await axiosInstance.get(`/api/admin/departments/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in getById:', error);
            throw error;
        }
    },

    // Create new department
    async create(data: Partial<Department>): Promise<DepartmentResponse> {
        try {
            const response = await axiosInstance.post('/api/admin/departments', data);
            return response.data;
        } catch (error) {
            console.error('Error in create:', error);
            throw error;
        }
    },

    // Update department
    async update(id: number, data: Partial<Department>): Promise<DepartmentResponse> {
        try {
            const response = await axiosInstance.put(`/api/admin/departments/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error in update:', error);
            throw error;
        }
    },

    // Delete department
    async delete(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.delete(`/api/admin/departments/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in delete:', error);
            throw error;
        }
    },

    // Bulk delete departments
    async bulkDelete(ids: number[]): Promise<{ success: boolean; message: string; deleted_count?: number }> {
        try {
            const response = await axiosInstance.post('/api/admin/departments/bulk-delete', { ids });
            return response.data;
        } catch (error) {
            console.error('Error in bulkDelete:', error);
            throw error;
        }
    },

    // Update status
    async updateStatus(id: number, isActive: boolean): Promise<{ success: boolean; message: string; data: any }> {
        try {
            const response = await axiosInstance.patch(`/api/admin/departments/${id}/status`, {
                is_active: isActive
            });
            return response.data;
        } catch (error) {
            console.error('Error in updateStatus:', error);
            throw error;
        }
    },

    // Reorder departments
    async reorder(orders: { id: number; order: number }[]): Promise<{ success: boolean; message: string }> {
        try {
            const response = await axiosInstance.post('/api/admin/departments/reorder', { orders });
            return response.data;
        } catch (error) {
            console.error('Error in reorder:', error);
            throw error;
        }
    }
};